/**
 * Member Services Page
 *
 * Integrated view for members who are employees showing:
 * - Referrals (service referrals from CBOs)
 * - Program Enrollments
 * - Trust Program Services
 *
 * This provides an integrated experience for employees to access
 * member services without navigating to a separate portal.
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ClipboardList,
  CheckCircle,
  FileText,
  Users,
  Heart,
  Clock,
  AlertCircle,
  ArrowRight,
  Calendar,
  MapPin,
  Phone,
  ExternalLink,
  ChevronRight,
  Loader2,
  Briefcase,
  Upload,
  MessageSquare,
  Star,
  Bell,
  ShieldCheck,
} from 'lucide-react';
import { getMemberData, getMemberToken, getDashboard, type MemberDashboard } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import EmployeePortalLayout from '../layout';
import { EmployeeConsentFlow } from '@/components/service-referrals/EmployeeConsentFlow';

// Types
interface Referral {
  id: number;
  referral_number: string;
  service_type: string;
  provider_name: string;
  provider_address?: string;
  provider_phone?: string;
  status: 'pending' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  created_date: string;
  scheduled_date?: string;
  notes?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

interface Enrollment {
  id: number;
  program_name: string;
  program_type: string;
  organization_name: string;
  status: 'active' | 'pending' | 'completed' | 'withdrawn';
  enrolled_date: string;
  end_date?: string;
  progress_percentage?: number;
  next_action?: string;
}

interface TrustService {
  id: number;
  service_name: string;
  service_type: string;
  description: string;
  status: 'available' | 'enrolled' | 'pending' | 'completed';
  enrollment_url?: string;
  contact_phone?: string;
  incentive_amount?: number;
}

interface PendingOffer {
  id: number;
  program_name: string;
  program_description?: string;
  employer_name: string;
  offered_at: string;
  expires_at?: string;
  services_included?: string[];
}

const tabs = [
  { id: 'pending-offers', label: 'Pending Offers', icon: Bell, badge: true },
  { id: 'referrals', label: 'My Referrals', icon: ClipboardList },
  { id: 'enrollments', label: 'Program Enrollments', icon: CheckCircle },
  { id: 'trust-services', label: 'Trust Services', icon: Heart },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'work-compliance', label: 'Work Requirements', icon: Briefcase },
];

export default function MemberServicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'pending-offers');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<MemberDashboard | null>(null);

  // Data states
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [trustServices, setTrustServices] = useState<TrustService[]>([]);
  const [pendingOffers, setPendingOffers] = useState<PendingOffer[]>([]);
  const [selectedOfferId, setSelectedOfferId] = useState<number | null>(null);
  const [showConsentFlow, setShowConsentFlow] = useState(false);

  const member = getMemberData();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!member?.id) {
        setLoading(false);
        return;
      }

      try {
        const token = getMemberToken();
        if (!token) {
          setLoading(false);
          return;
        }

        // Fetch dashboard data
        const dashboardData = await getDashboard();
        setDashboard(dashboardData);

        // Fetch referrals
        try {
          const referralsResponse = await fetch(
            getWeaveinApiUrl(`/api/member-portal/referrals`),
            {
              headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': localStorage.getItem('active_cbo_id') || '', 'Content-Type': 'application/json' },
            }
          );
          if (referralsResponse.ok) {
            setReferrals(await referralsResponse.json());
          } else {
            // Mock data
            setReferrals([
              {
                id: 1,
                referral_number: 'REF-2025-001234',
                service_type: 'Mental Health Counseling',
                provider_name: 'Community Mental Health Center',
                provider_address: '123 Wellness Way, Suite 200',
                provider_phone: '(555) 123-4567',
                status: 'scheduled',
                created_date: '2025-11-15',
                scheduled_date: '2025-12-20',
                priority: 'medium',
              },
              {
                id: 2,
                referral_number: 'REF-2025-001198',
                service_type: 'Nutrition Counseling',
                provider_name: 'Healthy Living Clinic',
                provider_address: '456 Health Ave',
                provider_phone: '(555) 234-5678',
                status: 'in_progress',
                created_date: '2025-10-20',
                priority: 'low',
                notes: '3 sessions completed, 3 remaining',
              },
              {
                id: 3,
                referral_number: 'REF-2025-001156',
                service_type: 'Housing Assistance',
                provider_name: 'Community Housing Services',
                provider_phone: '(555) 345-6789',
                status: 'completed',
                created_date: '2025-09-01',
                priority: 'high',
              },
            ]);
          }
        } catch (err) {
          console.error('Error fetching referrals:', err);
        }

        // Fetch enrollments
        try {
          const enrollmentsResponse = await fetch(
            getWeaveinApiUrl(`/api/member-portal/enrollments`),
            {
              headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': localStorage.getItem('active_cbo_id') || '', 'Content-Type': 'application/json' },
            }
          );
          if (enrollmentsResponse.ok) {
            setEnrollments(await enrollmentsResponse.json());
          } else {
            // Mock data
            setEnrollments([
              {
                id: 1,
                program_name: 'Chronic Care Management',
                program_type: 'Health Management',
                organization_name: 'Trust Health Network',
                status: 'active',
                enrolled_date: '2025-06-15',
                progress_percentage: 45,
                next_action: 'Schedule quarterly check-in',
              },
              {
                id: 2,
                program_name: 'Financial Wellness Program',
                program_type: 'Financial Education',
                organization_name: 'Community CBO',
                status: 'active',
                enrolled_date: '2025-08-01',
                progress_percentage: 75,
                next_action: 'Complete Module 4',
              },
              {
                id: 3,
                program_name: 'Job Readiness Training',
                program_type: 'Employment Services',
                organization_name: 'Workforce Development Center',
                status: 'completed',
                enrolled_date: '2025-03-01',
                end_date: '2025-09-30',
                progress_percentage: 100,
              },
            ]);
          }
        } catch (err) {
          console.error('Error fetching enrollments:', err);
        }

        // Mock Trust services
        setTrustServices([
          {
            id: 1,
            service_name: 'Care Navigation',
            service_type: 'Support Service',
            description: 'Get personalized help navigating your health benefits and finding the right care.',
            status: 'available',
            contact_phone: '1-800-TRUST-01',
          },
          {
            id: 2,
            service_name: 'Health Rewards Program',
            service_type: 'Incentive Program',
            description: 'Earn rewards for completing health actions and wellness activities.',
            status: 'enrolled',
            incentive_amount: 250,
          },
          {
            id: 3,
            service_name: 'Virtual Health Coaching',
            service_type: 'Wellness',
            description: 'One-on-one sessions with certified health coaches for lifestyle improvement.',
            status: 'available',
            enrollment_url: '/employee-portal/benefits?tab=programs',
          },
          {
            id: 4,
            service_name: 'Social Determinants Screening',
            service_type: 'Assessment',
            description: 'Complete a brief assessment to identify additional support resources.',
            status: 'pending',
          },
        ]);

        // Fetch pending program offers (consents awaiting employee approval)
        try {
          const offersResponse = await fetch(
            getWeaveinApiUrl(`/api/employee-consent/offers/pending`),
            {
              headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': localStorage.getItem('active_cbo_id') || '', 'Content-Type': 'application/json' },
            }
          );
          if (offersResponse.ok) {
            const offersData = await offersResponse.json();
            setPendingOffers(offersData);
          }
        } catch (err) {
          console.error('Error fetching pending offers:', err);
          // Keep empty array on error
        }
      } catch (error) {
        console.error('Error fetching member services data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [member?.id]);

  // Handle consent completion
  const handleConsentComplete = (consentId: number) => {
    setShowConsentFlow(false);
    setSelectedOfferId(null);
    // Refresh pending offers
    setPendingOffers(prev => prev.filter(o => o.id !== selectedOfferId));
    // Optionally switch to enrollments tab
    setActiveTab('enrollments');
  };

  const handleConsentDecline = () => {
    setShowConsentFlow(false);
    setSelectedOfferId(null);
    // Refresh pending offers
    setPendingOffers(prev => prev.filter(o => o.id !== selectedOfferId));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'enrolled':
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'pending':
      case 'scheduled':
        return 'bg-amber-100 text-amber-700';
      case 'in_progress':
        return 'bg-blue-100 text-blue-700';
      case 'cancelled':
      case 'withdrawn':
        return 'bg-red-100 text-red-700';
      case 'available':
        return 'bg-indigo-100 text-indigo-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-300';
      default: return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  if (loading) {
    return (
      <EmployeePortalLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </EmployeePortalLayout>
    );
  }

  const activeReferrals = referrals.filter(r => r.status !== 'completed' && r.status !== 'cancelled').length;
  const activeEnrollments = enrollments.filter(e => e.status === 'active').length;

  return (
    <EmployeePortalLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Member Services</h1>
          <p className="text-gray-600 mt-1">
            Access your referrals, program enrollments, and Trust services.
          </p>
        </div>

        {/* Pending Offers Alert Banner */}
        {pendingOffers.length > 0 && (
          <div
            className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => handleTabChange('pending-offers')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-amber-100 rounded-full animate-pulse">
                  <Bell className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-900">
                    {pendingOffers.length} Program Offer{pendingOffers.length > 1 ? 's' : ''} Awaiting Your Consent
                  </h3>
                  <p className="text-sm text-amber-700">
                    Review and accept to unlock your employer-sponsored benefits
                  </p>
                </div>
              </div>
              <button className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 flex items-center">
                Review Now <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div
            className={`bg-white rounded-xl shadow-sm border p-4 cursor-pointer hover:shadow-md transition-shadow ${
              pendingOffers.length > 0 ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
            }`}
            onClick={() => handleTabChange('pending-offers')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Offers</p>
                <p className={`text-2xl font-bold ${pendingOffers.length > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                  {pendingOffers.length}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${pendingOffers.length > 0 ? 'bg-amber-100' : 'bg-gray-100'}`}>
                <Bell className={`w-5 h-5 ${pendingOffers.length > 0 ? 'text-amber-600' : 'text-gray-400'}`} />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Referrals</p>
                <p className="text-2xl font-bold text-gray-900">{activeReferrals}</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <ClipboardList className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Program Enrollments</p>
                <p className="text-2xl font-bold text-gray-900">{activeEnrollments}</p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Documents</p>
                <p className="text-2xl font-bold text-gray-900">{dashboard?.total_documents || 0}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Messages</p>
                <p className="text-2xl font-bold text-gray-900">{dashboard?.unread_communications || 0}</p>
                <p className="text-xs text-gray-500">unread</p>
              </div>
              <div className="p-2 bg-amber-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex -mb-px" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const showBadge = tab.id === 'pending-offers' && pendingOffers.length > 0;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`
                      flex items-center px-4 py-4 text-sm font-medium border-b-2 whitespace-nowrap relative
                      ${isActive
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className={`w-4 h-4 mr-2 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                    {tab.label}
                    {showBadge && (
                      <span className="ml-2 px-1.5 py-0.5 text-xs font-bold rounded-full bg-red-500 text-white">
                        {pendingOffers.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {/* Pending Offers Tab - Consent Workflow */}
            {activeTab === 'pending-offers' && (
              <div className="space-y-6">
                {showConsentFlow && selectedOfferId ? (
                  <EmployeeConsentFlow
                    employeeId={member?.employee_id}
                    offerId={selectedOfferId}
                    onConsentComplete={handleConsentComplete}
                    onDecline={handleConsentDecline}
                  />
                ) : pendingOffers.length > 0 ? (
                  <>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start space-x-3">
                      <Bell className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-amber-800">Action Required</h3>
                        <p className="text-sm text-amber-700">
                          You have {pendingOffers.length} pending program offer{pendingOffers.length > 1 ? 's' : ''} awaiting your consent.
                          Review and accept to access these benefits.
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-4">
                      {pendingOffers.map((offer) => (
                        <div
                          key={offer.id}
                          className="border border-indigo-200 rounded-xl p-6 bg-gradient-to-r from-indigo-50 to-white hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-indigo-100 rounded-lg">
                                <ShieldCheck className="w-6 h-6 text-indigo-600" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-lg text-gray-900">{offer.program_name}</h3>
                                <p className="text-sm text-gray-500">Offered by {offer.employer_name}</p>
                              </div>
                            </div>
                            <span className="px-3 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                              Awaiting Consent
                            </span>
                          </div>

                          {offer.program_description && (
                            <p className="text-gray-700 mb-4">{offer.program_description}</p>
                          )}

                          {offer.services_included && offer.services_included.length > 0 && (
                            <div className="mb-4">
                              <p className="text-sm text-gray-500 mb-2">Services Included:</p>
                              <div className="flex flex-wrap gap-2">
                                {offer.services_included.map((service, idx) => (
                                  <span key={idx} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                                    {service}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <div className="text-sm text-gray-500">
                              <Clock className="w-4 h-4 inline mr-1" />
                              Offered on {new Date(offer.offered_at).toLocaleDateString()}
                              {offer.expires_at && (
                                <span className="text-amber-600 ml-2">
                                  Expires {new Date(offer.expires_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                setSelectedOfferId(offer.id);
                                setShowConsentFlow(true);
                              }}
                              className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center"
                            >
                              Review & Accept <ArrowRight className="w-4 h-4 ml-2" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">All Caught Up!</h3>
                    <p className="text-gray-500">
                      You have no pending program offers at this time.
                      Check back later for new benefit opportunities from your employer.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Referrals Tab */}
            {activeTab === 'referrals' && (
              <div className="space-y-4">
                {referrals.length > 0 ? (
                  referrals.map((referral) => (
                    <div
                      key={referral.id}
                      className={`border rounded-xl p-4 ${
                        referral.priority === 'urgent' ? 'border-red-300 bg-red-50' :
                        referral.priority === 'high' ? 'border-orange-300 bg-orange-50' :
                        'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-gray-900">{referral.service_type}</h3>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(referral.status)}`}>
                              {referral.status.replace('_', ' ')}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getPriorityColor(referral.priority)}`}>
                              {referral.priority}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">#{referral.referral_number}</p>
                        </div>
                        <button className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">
                          View Details
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Provider</p>
                          <p className="font-medium text-gray-900">{referral.provider_name}</p>
                          {referral.provider_address && (
                            <p className="text-gray-600 flex items-center mt-1">
                              <MapPin className="w-3 h-3 mr-1" />
                              {referral.provider_address}
                            </p>
                          )}
                          {referral.provider_phone && (
                            <p className="text-indigo-600 flex items-center mt-1">
                              <Phone className="w-3 h-3 mr-1" />
                              {referral.provider_phone}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-gray-500">Created</p>
                          <p className="font-medium text-gray-900">
                            {new Date(referral.created_date).toLocaleDateString()}
                          </p>
                          {referral.scheduled_date && (
                            <>
                              <p className="text-gray-500 mt-2">Scheduled</p>
                              <p className="font-medium text-gray-900 flex items-center">
                                <Calendar className="w-3 h-3 mr-1" />
                                {new Date(referral.scheduled_date).toLocaleDateString()}
                              </p>
                            </>
                          )}
                        </div>
                        {referral.notes && (
                          <div>
                            <p className="text-gray-500">Notes</p>
                            <p className="text-gray-700">{referral.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No referrals found.</p>
                  </div>
                )}
              </div>
            )}

            {/* Enrollments Tab */}
            {activeTab === 'enrollments' && (
              <div className="space-y-4">
                {enrollments.length > 0 ? (
                  enrollments.map((enrollment) => (
                    <div key={enrollment.id} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-gray-900">{enrollment.program_name}</h3>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(enrollment.status)}`}>
                              {enrollment.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">{enrollment.organization_name}</p>
                        </div>
                        <button className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">
                          View Details
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Program Type</p>
                          <p className="font-medium text-gray-900">{enrollment.program_type}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Enrolled</p>
                          <p className="font-medium text-gray-900">
                            {new Date(enrollment.enrolled_date).toLocaleDateString()}
                          </p>
                          {enrollment.end_date && (
                            <p className="text-gray-600 text-xs">
                              Ended: {new Date(enrollment.end_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        {enrollment.progress_percentage !== undefined && (
                          <div>
                            <p className="text-gray-500 mb-1">Progress</p>
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-green-500 h-2 rounded-full transition-all"
                                  style={{ width: `${enrollment.progress_percentage}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium text-gray-700">{enrollment.progress_percentage}%</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {enrollment.next_action && enrollment.status === 'active' && (
                        <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center">
                          <AlertCircle className="w-4 h-4 text-amber-600 mr-2" />
                          <span className="text-sm text-amber-800">Next: {enrollment.next_action}</span>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No program enrollments found.</p>
                  </div>
                )}
              </div>
            )}

            {/* Trust Services Tab */}
            {activeTab === 'trust-services' && (
              <div className="grid md:grid-cols-2 gap-4">
                {trustServices.map((service) => (
                  <div key={service.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Heart className="w-5 h-5 text-pink-500" />
                        <h3 className="font-semibold text-gray-900">{service.service_name}</h3>
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(service.status)}`}>
                        {service.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-1">{service.service_type}</p>
                    <p className="text-sm text-gray-700 mb-3">{service.description}</p>

                    {service.incentive_amount && (
                      <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center">
                        <Star className="w-4 h-4 text-green-600 mr-2" />
                        <span className="text-sm text-green-800">Earn up to ${service.incentive_amount} in rewards</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      {service.contact_phone && (
                        <a href={`tel:${service.contact_phone}`} className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center">
                          <Phone className="w-3 h-3 mr-1" />
                          {service.contact_phone}
                        </a>
                      )}
                      {service.status === 'available' && (
                        <Link
                          to={service.enrollment_url || '#'}
                          className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center"
                        >
                          Enroll Now <ArrowRight className="w-3 h-3 ml-1" />
                        </Link>
                      )}
                      {service.status === 'enrolled' && (
                        <span className="text-sm text-green-600 flex items-center">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Currently Enrolled
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <p className="text-gray-600">{dashboard?.total_documents || 0} documents uploaded</p>
                  <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center">
                    <Upload className="w-4 h-4 mr-1" />
                    Upload Document
                  </button>
                </div>

                <div className="border border-dashed border-gray-300 rounded-xl p-8 text-center">
                  <Upload className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 mb-2">Drag and drop files here, or click to browse</p>
                  <p className="text-sm text-gray-500">Supported formats: PDF, JPG, PNG (max 10MB)</p>
                  <button className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
                    Browse Files
                  </button>
                </div>

                <div className="text-center py-8">
                  <p className="text-gray-500">
                    For full document management, visit the{' '}
                    <Link to="/member/documents" className="text-indigo-600 hover:text-indigo-700 font-medium">
                      Member Portal Documents
                    </Link>
                  </p>
                </div>
              </div>
            )}

            {/* Work Requirements Tab */}
            {activeTab === 'work-compliance' && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Briefcase className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">Medicaid Work Requirements</h3>
                      <p className="text-sm text-blue-800 mb-3">
                        Track your work activity hours and maintain compliance with Medicaid work requirements.
                      </p>
                      <Link
                        to="/member/work-compliance"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                      >
                        Go to Work Compliance Dashboard <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border border-gray-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-gray-600">Current Month Hours</p>
                    <p className="text-3xl font-bold text-gray-900">65</p>
                    <p className="text-sm text-green-600">/ 80 required</p>
                  </div>
                  <div className="border border-gray-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-gray-600">Compliance Status</p>
                    <div className="flex items-center justify-center mt-2">
                      <CheckCircle className="w-6 h-6 text-green-500 mr-2" />
                      <span className="text-lg font-semibold text-green-700">Compliant</span>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-gray-600">Next Reporting Due</p>
                    <p className="text-lg font-semibold text-gray-900">Dec 31, 2025</p>
                    <p className="text-sm text-amber-600">22 days remaining</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </EmployeePortalLayout>
  );
}
